import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ConfigurationService } from './core/services/configurationService';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateCustomLoader } from '@translateCustomLoader';

import { AppComponent } from './app.component';

import { ThemeModule } from '@shared/modules/theme/theme.module';

import { ThemeService } from '@shared/modules/theme/services/theme.service';

import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from '@shared/shared.module';
import { CoreModule } from './core/core.module';
import { DataListService } from '@shared/services/dataList.service';
import { forkJoin } from 'rxjs';
import { AppConfigService } from './app.config.service';
import { GoogleMapsModule } from '@angular/google-maps';
import { AuthService } from '@shared/services/authService';
import { IAuthData } from '@shared/interfaces/IAuthData';
import { WebchatService } from '@shared/services/webchat.service';
import { AuthScope } from '@shared/enum/AuthScope.enum';
import { AuthLob } from '@shared/enum/AuthLob.enum';
import { TrackifyDefinesComplexVersion, TrackifyDefinesSimpleVersion, TrackifyWindow } from '@allianz/tracking/models/trackify.model';
import { TrackingGlobals } from '@shared/enum/trackingGlobals';
import { DynatraceLogService } from '@coreModule/services/dynatraceLog.service';
import { v4 as uuidv4 } from 'uuid';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AesService } from '@shared/services/aes.service';
import { ScreenName } from '@shared/enum/screenName.enum';
import { LowerCaseUrlSerializer } from '@coreModule/providers/lowerCaseUrlSerializer';
import { UrlSerializer } from '@angular/router';
import { ExternalParamUtil } from '@coreModule/utils/externalParamUtil';
import { DynatraceLogActionName } from '@shared/enum/dynatraceLogActionName.enum';

export function initializeAppConfigAndTheme(
  appConfigService: AppConfigService,
  authService: AuthService,
  configurationService: ConfigurationService,
  themeService: ThemeService
) {
  return async (): Promise<void> => {
    // Generate SessionId
    AesService.setScreenName(ScreenName.APP_MODULE);
    const sessionIdFromLocalStorage = localStorage.getItem('cwaUserSessionId');
    if (!sessionIdFromLocalStorage) {
      const uuid = uuidv4();
      localStorage.setItem('cwaUserSessionId', uuid);
    }

    await appConfigService.load(); // preload configmap if present otherwise will use environment configs

    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const authResponse = await authService.authorize();
        const authData: IAuthData = { ...authResponse, lob: AuthLob[authResponse.lob], feature: AuthScope[authResponse.feature] };
        ConfigurationService.authData = authData;
        if (authResponse?.partnerId) {
          sessionStorage.setItem('PartnerID', authResponse.partnerId);
        }

        const configAndTheme = await configurationService.loadAppConfigAndTheme(authData);
        configurationService.setConfigs(configAndTheme['configs']);
        themeService.setThemes(configAndTheme['themes']);
      } catch (err) {
        if (err?.error?.validationResult?.code === 'TOKEN_NOT_FOUND') {
          configurationService.windowLocationReplace('401.html');
        } else if (err?.error?.errorCode === 'UNKNOWN_PARTNERID') {
          configurationService.windowLocationReplace('404.html');
        } else {
          configurationService.windowLocationReplace('500.html');
        }
      }
    } else {
      await forkJoin({
        appTenantConfig: configurationService.loadAppConfig(),
        themeConfig: themeService.loadThemes()
      })
        .toPromise()
        .then((response) => {
          const appTenantConfig = { ...response.appTenantConfig } as any;
          const themeConfig = response.themeConfig;

          if ([appTenantConfig?.error?.errorCode, themeConfig?.error?.errorCode].indexOf('UNKNOWN_PARTNERID') > -1) {
            return configurationService.windowLocationReplace('404.html');
          }
          themeService.setTheme();
          return response;
        })
        .catch((err) => {
          console.log(err);
          configurationService.windowLocationReplace('500.html');
        });
    }
  };
}

declare const window: TrackifyWindow;

export function loadTrackifyCustomMapping() {
  return () => {
    const pathPrefix = 'spaData.cwa.';
    const trackingPathMapping = {
      [`${pathPrefix}${TrackingGlobals.eventNames.GDPR_ENABLED}`]: 'user.consent.gdprEnabled',
      [`${pathPrefix}${TrackingGlobals.eventNames.COOKIE_CONSENT}`]: 'user.consent.isAny',
      [`${pathPrefix}${TrackingGlobals.eventNames.HOSTNAME}`]: 'page.pageInfo.hostname',
      [`${pathPrefix}${TrackingGlobals.eventNames.PAGE_URL}`]: 'page.pageInfo.URL',
      [`${pathPrefix}${TrackingGlobals.eventNames.FULL_URL}`]: 'page.pageInfo.fullURL',
      [`${pathPrefix}${TrackingGlobals.eventNames.SITE_SECTION}`]: 'page.pageInfo.siteSection',
      [`${pathPrefix}${TrackingGlobals.eventNames.TITLE}`]: 'page.pageInfo.title',
      [`${pathPrefix}${TrackingGlobals.eventNames.BUSINESS_ENTITY}`]: 'page.attributes.organisationalEntity',
      [`${pathPrefix}${TrackingGlobals.eventNames.FEATURE}`]: 'application.name',
      [`${pathPrefix}${TrackingGlobals.EVENT_APPLICATION_ID}`]: 'application.Id',
      [`${pathPrefix}${TrackingGlobals.eventNames.APPLICATION_VERSION}`]: 'application.version',
      [`${pathPrefix}${TrackingGlobals.EVENT_APPLICATION_CONTEXT}`]: 'application.context',
      [`${pathPrefix}${TrackingGlobals.eventNames.PAGE_NAME}`]: 'page.pageInfo.pageName',
      [`${pathPrefix}${TrackingGlobals.eventNames.PREVIOUS_PAGE_NAME}`]: 'page.previousPage',
      [`${pathPrefix}${TrackingGlobals.eventNames.VIRTUAL_PAGE_NAME}`]: 'application.virtualPageName',
      [`${pathPrefix}${TrackingGlobals.eventNames.PREVIOUS_VIRTUAL_PAGE_NAME}`]: 'application.previousVirtualPageName',
      [`${pathPrefix}${TrackingGlobals.eventNames.PROCESS_NAME}`]: 'process.processName',
      [`${pathPrefix}${TrackingGlobals.eventNames.STEP_NAME}`]: 'process.stepName',
      [`${pathPrefix}${TrackingGlobals.eventNames.PREVIOUS_STEP_NAME}`]: 'process.previousStepName',
      [`${pathPrefix}${TrackingGlobals.eventNames.STEP_NUMBER}`]: 'process.stepNumber',
      [`${pathPrefix}${TrackingGlobals.eventNames.PREVIOUS_STEP_NUMBER}`]: 'process.previousStepNumber',
      [`${pathPrefix}${TrackingGlobals.eventNames.EVENT_CATEGORY}`]: 'event.category',
      [`${pathPrefix}${TrackingGlobals.eventNames.EVENT_LABEL}`]: 'event.label',
      [`${pathPrefix}${TrackingGlobals.eventNames.EVENT_ACTION}`]: 'event.action',
      [`${pathPrefix}${TrackingGlobals.eventNames.EVENT_VALUE}`]: 'event.value',
      [`${pathPrefix}${TrackingGlobals.eventNames.ERROR_TYPE}`]: 'error.type',
      [`${pathPrefix}${TrackingGlobals.eventNames.ERROR_MESSAGE}`]: 'error.message',
      [`${pathPrefix}${TrackingGlobals.eventNames.ERROR_CODE}`]: 'error.code',
      [`${pathPrefix}${TrackingGlobals.eventNames.MEDIA_UPLOAD_TYPE}`]: 'media.upload.type',
      [`${pathPrefix}${TrackingGlobals.eventNames.MEDIA_UPLOAD_FILE_TYPE}`]: 'media.upload.fileType',
      [`${pathPrefix}${TrackingGlobals.eventNames.MEDIA_UPLOAD_FILE_SIZE}`]: 'media.upload.fileSize'
    } as TrackifyDefinesSimpleVersion | TrackifyDefinesComplexVersion;
    if (!window.trackifyDefines) {
      // be safe to not override custom settings done by a tracking expert
      window.trackifyDefines = trackingPathMapping;
    } else {
      // merge with custom settings done by a tracking expert
      // using this order, Angular custom mapping potentially overrides an existing rewrite for an event name setup in Launch
      window.trackifyDefines = Object.assign(window.trackifyDefines, trackingPathMapping);
      // if we want the Launch custom mappings to be taken with higher relevance:
      // window.trackifyDefines = Object.assign(trackingPathMapping, window.trackifyDefines);
    }
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateCustomLoader,
        deps: [HttpClient, AppConfigService, DynatraceLogService]
      }
    }),
    GoogleMapsModule,
    ThemeModule.forRoot({
      themes: ThemeService.themeProps,
      active: 'default'
    }),
    SharedModule,
    CoreModule
  ],
  providers: [
    AppConfigService,
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppConfigAndTheme,
      deps: [AppConfigService, AuthService, ConfigurationService, ThemeService],
      multi: true
    },
    AuthService,
    ThemeService,
    DataListService,
    WebchatService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadTrackifyCustomMapping,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private dynatraceLogService: DynatraceLogService) {
    const source = ExternalParamUtil.getSourceParam();
    if (source) {
      this.dynatraceLogService.sendAction(DynatraceLogActionName.RECEIVE_SOURCE, { source: { value: source, public: true } });
      ExternalParamUtil.validateAllowedSourceParam(source);
    }
  }
}
